import {Component, NgZone, OnInit} from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router
} from '@angular/router';
import {fromEvent, merge, Observable, of, Subscription, throwError} from 'rxjs';
import {AuthService} from '@app/+auth/shared/services/auth.service';
import {Path} from '@core/enums';
import {SeoService} from '@core/services';
import {catchError, debounceTime, filter, switchMap} from 'rxjs/operators';
import {Meta, Title} from '@angular/platform-browser';
import {environment} from '@environments/environment';
import {SessionService} from '@core/services/session.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {SessionWarningDialogComponent} from '@core/dialog/session-warning-dialog/session-warning-dialog.component';
import {ToastrService} from 'ngx-toastr';
import * as moment from 'moment';

// tslint:disable-next-line:ban-types
declare const gtag: Function; // <------------Important: the declartion for gtag is required!

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {

  constructor(
    public router: Router,
    private seoService: SeoService,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private meta: Meta,
    private sessionService: SessionService,
  ) {
    // Meta tag for google description
    this.meta.addTag({
      name: 'description',
      content: `Gabadoo relieves the stress of the unknown by connecting you with experienced developmental experts in a quick and affordable way.`
    });
    this.isLoggedIn$ = this.authService.isLoggedIn.asObservable();

    router.events.subscribe(event => {
      this.navigationInterceptor(event);
    });

    const gaTrackId = environment.googleAnalyticsKey;

    this.loadGoogleAnalytics(gaTrackId);

    // call this function first , which will load the gtag.js on base of different track Id in async manner
    // this.grabTheTrackId();

    /** START : Code to Track Page View using gtag.js */
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      gtag('event', 'page_view', {
        page_path: event.urlAfterRedirects
      });
      // Earlier
      //  gtag('config', googleAnalyticsId,  { page_path: event.urlAfterRedirects});
    });
    /** END : Code to Track Page View  using gtag.js */

    // Add dynamic title for selected pages - Start
    router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const rootValue = router.routerState.root;
        let title = this.getTitle(router.routerState, rootValue).join(' > ');
        if (title.includes('draft-gabadoo')){
          title = '/draft-gabadoo';
        }else if (title.includes('child-profile')) {
          title = '/child-profile';
        }else if (title.includes('gaba-plan/strategies')) {
          title = '/gaba-plan/strategies';
        }else if (title.includes('gaba-plan/products')) {
          title = '/gaba-plan/products';
        }else if (title.includes('gaba-plan/resources')) {
          title = '/gaba-plan/resources';
        }else if (title.includes('gaba-plan')) {
          title = '/gaba-plan';
        } else if (title.includes('child-profile')) {
          title = '/child-profile';
        }else if (title.includes('child-profile')) {
          title = '/child-profile';
        }else if (title.includes('continue-gabadoo')) {
          title = '/continue-gabadoo';
        }else if (title.includes('gaba-link')) {
          title = '/gaba-link';
        }else if (title.includes('review-gabadoo')) {
          title = '/review-gabadoo';
        }
        // Partner Page URL
        else if (title.includes('partner/gabadoo/plan')) {
          title = 'Partner - Gaba Plan Information';
        }

        // Update the title
        switch (title) {
          case '/':
            title = 'Home';
            break;
          case '/create-gabadoo':
            title = 'Create Gabadoo';
            break;
          case '/gabadoo':
            title = 'Gabadoo';
            break;
          case '/draft-gabadoo':
            title = 'Draft Gabadoo';
            break;
          case '/create-child':
            title = 'Create Child';
            break;
          case '/child-profile':
            title = 'Child Profile';
            break;
          case '/gaba-plan':
            title = 'Gaba Plan';
            break;
          case '/gaba-plan/strategies':
            title = 'Gaba Plan Strategies';
            break;
          case '/gaba-plan/resources':
            title = 'Gaba Plan Resources';
            break;
          case '/gaba-plan/products':
            title = 'Gaba Plan Products';
            break; // pending
          case 'therapist/profile':
            title = 'Therapist Profile';
            break;
          case '/payment-plan':
            title = 'Payment Plan';
            break;
          case '/payment-success':
            title = 'Payment Success';
            break;
          case '/account':
            title = 'Parent Account';
            break;
          case '/continue-gabadoo':
            title = 'Continue Gabadoo';
            break;
          case '/gaba-link':
            title = 'Gaba Link';
            break;
          case '/review-gabadoo':
            title = 'Review Gabadoo';
            break;
          case '/sign-in':
            title = 'Sign In';
            break;
          case '/sign-up':
            title = 'Sign Up';
            break;
          case '/admin':
            title = 'Admin - Dashboard';
            break;
          case '/admin/add-gabadoo-credit':
            title = 'Admin - Add Gaba Credit';
            break;
          case '/admin/check-therapist-status':
            title = 'Admin - Therapist Status';
            break;
          case '/admin/add-interview-slots':
            title = 'Admin - Interview Slots';
            break;
          case '/admin/view-videos':
            title = 'Admin - View Videos';
            break;
          case '/admin/add-video':
            title = 'Admin - Add Video';
            break;
          case '/admin/add-organisation':
            title = 'Admin - Add Organisation';
            break;
          case '/admin/view-organisation':
            title = 'Admin - View Organisation';
            break;
          case '/admin/add-product':
            title = 'Admin - Add Product';
            break;
          case '/admin/view-products':
            title = 'Admin - View Products';
            break;
          case '/admin/create-account':
            title = 'Admin - Create Admin Account';
            break;
            // ******* PARTNER CHANGES *****
          case '/partner':
            title = 'Partner - Home Page';
            break;
          case '/partner/gabadoo':
            title = 'Partner - All Gabadoo';
            break;
          case '/partner/account':
            title = 'Partner - Account';
            break;
          case '/partner/gabadoo/plan':
            title = 'Partner - Gaba Plan Information';
            break;
          case '/admin/create-account':
            title = 'Admin - Create Admin Account';
            break;
        }
        // Set the title for analytics
        titleService.setTitle(title);
      }
    });
    // Add dynamic title for selected pages - End
  }
  isLoggedIn$: Observable<boolean>;
  // tslint:disable-next-line:ban-types
  isMobileDevice: Boolean;
  title = 'DigitalWorkerBees';
  loadingDataImg = false;

  ngOnInit(): void {
    this.seoService.init();
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    this.sessionService.initializeSessionTimers();
    this.sessionService.trackUserActivity();

    this.sessionService.sessionExpiration$.subscribe((isWarning) => {
      if (isWarning) {
        // Commenting out as the warning dialog needs improvement
      //  this.sessionService.openSessionWarningDialog();
      } else {
        this.sessionService.handleSessionExpired();
      }
    });
  }

  // collect that title data properties from all child routes
  // there might be a better way but this worked for me
  getTitle(state, parent) {
    const data = [];

    if (parent && parent.snapshot.data && parent.snapshot.data.title) {
      data.push(parent.snapshot.data.title);
    }else {
      if (parent && parent._routerState) {
        data.push(parent._routerState.snapshot.url);
      }
    }

    if (state && parent) {
      data.push(... this.getTitle(state, state.firstChild(parent))); // old code, need to check again
    }
    const finalDataSet  = new Set(data);
    return Array.from(finalDataSet);
  }

  // Shows and hides the loading spinner during RouterEvent changes
  navigationInterceptor(event: any): void {

    // Triggered When the navigation starts
    if (event instanceof NavigationStart) {
      this.loadingDataImg = true;
    }
    // Triggered When the navigation ends
    if (event instanceof NavigationEnd) {
      this.loadingDataImg = false;
    }

    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {
      this.loadingDataImg = false;
    }
    // Triggered When the error occurrs while navigation
    if (event instanceof NavigationError) {
      this.loadingDataImg = false;
    }
  }

  loadGoogleAnalytics(trackingID: string): void {

    const gaScript = document.createElement('script');
    gaScript.setAttribute('async', 'true');
    gaScript.setAttribute('src', `https://www.googletagmanager.com/gtag/js?id=${ trackingID }`);

    const gaScript2 = document.createElement('script');
    gaScript2.innerText = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag(\'js\', new Date());gtag(\'config\', \'${ trackingID }\', {send_page_view: false});`;

    document.documentElement.firstChild.appendChild(gaScript);
    document.documentElement.firstChild.appendChild(gaScript2);
  }

  // Old method
  grabTheTrackId(){
    // Read the value of track id obtained in the global config variable.
    // const gaTrackId = this.config.getConfig('trackId');
    // const gaTrackId = 'G-KSMC5Q0BEH';
    const gaTrackId = environment.googleAnalyticsKey;
    // Add custom element of script with following attributes
    const customGtagScriptEle: HTMLScriptElement = document.createElement('script');
    customGtagScriptEle.async = true;
    customGtagScriptEle.src = 'https://www.googletagmanager.com/gtag/js?id=' + gaTrackId;
    document.head.prepend(customGtagScriptEle);
    gtag('config', environment.googleAnalyticsKey, { send_page_view: false });
  }
}
